// Empire Apps Chassis · elder · types
// Branded types + interfaces for Sarathi (elder-first surface).
// All public type contracts live here so apps can import from one path.

export type Cd = string & { readonly __brand: "ContrastRatio" };
export type Pt = number & { readonly __brand: "Points" };
export type SelectorString = string & { readonly __brand: "CssSelector" };

export type LangCode = "hi" | "ta" | "bn" | "mr" | "gu" | "en";
export const SUPPORTED_LANGS = ["hi", "ta", "bn", "mr", "gu", "en"] as const;
export const DEFAULT_LANG: LangCode = "hi";
export const FALLBACK_LANG: LangCode = "en";

export type FontScale = 1.0 | 1.25 | 1.5 | 2.0;
export const FONT_SCALES = [1.0, 1.25, 1.5, 2.0] as const satisfies readonly FontScale[];

export interface ContrastResult {
  readonly ratio: number;
  readonly passesAALarge: boolean;   // >=3:1
  readonly passesAANormal: boolean;  // >=4.5:1
  readonly passesAAALarge: boolean;  // >=4.5:1
  readonly passesAAANormal: boolean; // >=7:1
  readonly suggestedFix: string | null;
}

export interface FontPreference {
  readonly scale: FontScale;
  readonly source: "os-prefers" | "user-explicit" | "default";
}

export interface BilingualText {
  readonly primary: string;
  readonly secondary: string;
  readonly primaryLang: LangCode;
  readonly secondaryLang: LangCode;
}

export type TutorTaskId =
  | "whatsapp.send_message"
  | "whatsapp.video_call"
  | "whatsapp.send_voice_note"
  | "phone.dial"
  | "camera.take_photo"
  | "gallery.share_photo"
  | "upi.send_money_to_contact"
  | "youtube.search_song"
  | "settings.font_size_increase"
  | "accessibility.enable_zoom";

export interface TutorStep {
  readonly id: string;
  readonly instructionKey: string;      // resolves via i18n-strings
  readonly highlightSelector: SelectorString | null;
  readonly waitForAction: "tap" | "scroll" | "wait" | "speak" | "none";
  readonly waitTimeoutMs: number;
  readonly retryMax: number;
}

export interface TutorTask {
  readonly id: TutorTaskId;
  readonly labelKey: string;            // i18n key for menu label
  readonly steps: readonly TutorStep[];
  readonly applicableApp: "whatsapp" | "phone" | "camera" | "gallery" | "upi" | "youtube" | "settings" | "accessibility";
}

export interface TutorRuntimeOptions {
  readonly lang: LangCode;
  readonly speechRate: "slow" | "normal" | "fast";
  readonly repeatOnError: boolean;
  readonly stepDelayMs: number;
  readonly highlightStyle: "halo" | "arrow" | "spotlight";
}

export type TutorEvent =
  | { readonly kind: "task-start"; readonly task: TutorTaskId }
  | { readonly kind: "step-start"; readonly stepIndex: number; readonly step: TutorStep }
  | { readonly kind: "speech-start"; readonly text: string; readonly lang: LangCode }
  | { readonly kind: "speech-end" }
  | { readonly kind: "awaiting-user"; readonly step: TutorStep }
  | { readonly kind: "user-action"; readonly action: TutorStep["waitForAction"] }
  | { readonly kind: "step-retry"; readonly stepIndex: number; readonly attempt: number }
  | { readonly kind: "step-skip"; readonly stepIndex: number }
  | { readonly kind: "task-complete"; readonly task: TutorTaskId }
  | { readonly kind: "task-aborted"; readonly reason: string };

export interface AnnounceOptions {
  readonly polite: boolean;
  readonly clearAfterMs: number;
}

export interface FocusTrapCleanup {
  readonly release: () => void;
}

// --- merged from F34 (phone-tutor-app types) ---

declare const __brand: unique symbol;
type Brand<T, B extends string> = T & { readonly [__brand]: B };

export type TaskId = Brand<string, "TaskId">;
export type BhajanId = Brand<string, "BhajanId">;
export type FamilyContactId = Brand<string, "FamilyContactId">;

export type TutorAction =
  | "tap"
  | "long-press"
  | "scroll"
  | "type"
  | "speak"
  | "wait"
  | "external-launch";

export interface TutorStep {
  readonly idx: number;
  readonly instruction_hi: string;
  readonly instruction_en: string;
  readonly voicePrompt_hi: string;
  readonly voicePrompt_en: string;
  readonly highlightSelector?: string;
  readonly waitForAction: TutorAction;
  readonly retryStrategy: "retry-3x" | "fallback-to-help-me" | "skip-with-warning";
  readonly safeguard?: string;
  readonly external?: { kind: "intent"; uri: string };
}

export type TaskCategory =
  | "messaging"
  | "calling"
  | "media"
  | "money"
  | "settings"
  | "accessibility"
  | "health"
  | "govt"
  | "bhakti"
  | "safety";

export interface TutorTask {
  readonly id: TaskId;
  readonly label_hi: string;
  readonly label_en: string;
  readonly category: TaskCategory;
  readonly minSdkLevel?: number;
  readonly requiresInternet: boolean;
  readonly avgDurationSec: number;
  readonly steps: readonly TutorStep[];
  readonly tags_hi: readonly string[];
  readonly tags_en: readonly string[];
}

export type LonelinessThreshold = "6h" | "12h" | "24h";

export interface LonelinessConfig {
  readonly enabled: boolean;
  readonly threshold: LonelinessThreshold;
  readonly quietHoursStart: number;
  readonly quietHoursEnd: number;
  readonly familyContacts: readonly FamilyContactId[];
  readonly escalateAt: LonelinessThreshold;
}

export const DEFAULT_LONELINESS: LonelinessConfig = {
  enabled: false,
  threshold: "6h",
  quietHoursStart: 22,
  quietHoursEnd: 6,
  familyContacts: [],
  escalateAt: "24h",
} as const;

export type ElderEventKind =
  | "elder.loneliness.threshold-breached"
  | "elder.loneliness.escalation"
  | "elder.loneliness.heartbeat"
  | "elder.tutor.started"
  | "elder.tutor.step-completed"
  | "elder.tutor.completed"
  | "elder.tutor.failed"
  | "elder.tutor.help-me-pressed"
  | "elder.bhakti.listened"
  | "elder.wa.message-translated"
  | "elder.wa.suspicious-message";

export interface ElderEvent {
  readonly id: string;
  readonly kind: ElderEventKind;
  readonly ts: number;
  readonly payload: Record<string, unknown>;
}

export interface BhajanEntry {
  readonly id: BhajanId;
  readonly title_hi: string;
  readonly title_en: string;
  readonly deity: string;
  readonly sect: "vaishnav" | "shaiv" | "shakt" | "smart" | "jain" | "sikh" | "buddhist" | "sufi" | "shared";
  readonly durationSec: number;
  readonly audioUrl: string;
  readonly transcript_hi?: string;
  readonly transcript_en?: string;
  readonly source: { name: string; url: string; license: string };
  readonly festivalTags: readonly string[];
}

export interface StoryEntry {
  readonly id: string;
  readonly title_hi: string;
  readonly title_en: string;
  readonly tradition: "ramayana" | "mahabharata" | "krishna-leela" | "hanuman" | "shiv-purana" | "devi-bhagavat" | "guru-bani" | "jataka" | "jain-katha" | "sufi";
  readonly chapter: string;
  readonly durationSec: number;
  readonly audioUrl?: string;
  readonly textUrl?: string;
  readonly narrator?: string;
  readonly source: { name: string; url: string; license: string };
}

export interface SimplifiedContact {
  readonly id: FamilyContactId;
  readonly displayName_hi: string;
  readonly displayName_en: string;
  readonly relation: "son" | "daughter" | "spouse" | "grandchild" | "sibling" | "other";
  readonly phone?: string;
  readonly waUri?: string;
  readonly defaultLang: LangShort;
}

export interface SuspiciousFlag {
  readonly score: number;
  readonly indicators: readonly string[];
  readonly action: "safe" | "caution" | "alert-family" | "block";
}

export function makeEventId(): string {
  const buf = new Uint8Array(12);
  crypto.getRandomValues(buf);
  return Array.from(buf).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export function asTaskId(s: string): TaskId {
  return s as TaskId;
}

export function asBhajanId(s: string): BhajanId {
  return s as BhajanId;
}

export function asFamilyContactId(s: string): FamilyContactId {
  return s as FamilyContactId;
}
